import React, { useEffect, useState } from "react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";
import BlockEditor from "../../components/BlockEditor";
import { Edit, X, ExternalLink, Plus, Trash2, Copy } from "lucide-react";

const SYSTEM = ["sobre-nosotros", "envios", "devoluciones"];
const EMPTY = { title: "", slug: "", content: "", meta_description: "", seo_title: "", blocks: [], menu: false };

export default function AdminPages() {
  const [pages, setPages] = useState([]);
  const [editing, setEditing] = useState(null);
  const isNew = editing && !editing.id;

  const load = () => adminApi.get("/admin/pages").then((r) => setPages(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (isNew) await adminApi.post("/admin/pages", editing);
      else await adminApi.put(`/admin/pages/${editing.id}`, editing);
      toast.success("Página guardada");
      setEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
  };

  const del = async (p) => {
    if (!window.confirm(`¿Eliminar la página "${p.title}"?`)) return;
    try { await adminApi.delete(`/admin/pages/${p.id}`); toast.success("Página eliminada"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "No se puede eliminar"); }
  };

  const dup = async (p) => {
    try { await adminApi.post(`/admin/pages/${p.id}/duplicate`); toast.success("Página duplicada"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error al duplicar"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading font-bold text-3xl tracking-tighter">Páginas del sitio</h1>
        <button onClick={() => setEditing({ ...EMPTY })} className="inline-flex items-center gap-2 bg-brand text-white rounded-sm px-4 py-2 text-sm font-semibold hover:bg-brand-dark" data-testid="new-page-btn"><Plus className="h-4 w-4" /> Nueva página</button>
      </div>
      <div className="bg-white border border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-brand-ink text-white"><tr><th className="text-left px-3 py-2">Título</th><th className="text-left px-3 py-2">Slug</th><th className="text-right px-3 py-2"></th></tr></thead>
          <tbody>
            {pages.map((p, i) => (
              <tr key={p.id} className={i % 2 ? "bg-secondary/40" : "bg-white"} data-testid={`page-row-${p.slug}`}>
                <td className="px-3 py-2">{p.title} {SYSTEM.includes(p.slug) && <span className="text-[10px] font-mono text-muted-foreground ml-1">(sistema)</span>}</td>
                <td className="px-3 py-2 font-mono text-xs text-muted-foreground">/pagina/{p.slug}</td>
                <td className="px-3 py-2 text-right">
                  <a href={`/pagina/${p.slug}`} target="_blank" rel="noreferrer" className="p-1.5 inline-block hover:text-brand"><ExternalLink className="h-4 w-4" /></a>
                  <button onClick={() => dup(p)} title="Duplicar" className="p-1.5 hover:text-brand" data-testid={`dup-page-${p.slug}`}><Copy className="h-4 w-4" /></button>
                  <button onClick={() => setEditing(p)} className="p-1.5 hover:text-brand"><Edit className="h-4 w-4" /></button>
                  {!SYSTEM.includes(p.slug) && <button onClick={() => del(p)} className="p-1.5 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-sm w-full max-w-2xl p-6 space-y-3 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between"><h2 className="font-heading font-semibold text-lg">{isNew ? "Nueva página" : "Editar página"}</h2><button onClick={() => setEditing(null)}><X className="h-5 w-5" /></button></div>
            <label className="text-sm font-medium block">Título</label>
            <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="w-full px-3 py-2 text-sm border border-border rounded-sm" data-testid="page-title" />
            {isNew && (<>
              <label className="text-sm font-medium block">Slug (URL) — opcional</label>
              <input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} placeholder="se genera del título" className="w-full px-3 py-2 text-sm border border-border rounded-sm font-mono" data-testid="page-slug" />
            </>)}
            <label className="text-sm font-medium block">Título SEO (meta title)</label>
            <input value={editing.seo_title || ""} onChange={(e) => setEditing({ ...editing, seo_title: e.target.value })} maxLength={70} placeholder="se usa el título si se deja vacío" className="w-full px-3 py-2 text-sm border border-border rounded-sm" data-testid="page-seo-title" />
            <label className="text-sm font-medium block">Meta description</label>
            <input value={editing.meta_description || ""} onChange={(e) => setEditing({ ...editing, meta_description: e.target.value })} maxLength={160} className="w-full px-3 py-2 text-sm border border-border rounded-sm" />
            <label className="text-sm font-medium block">Keywords (separadas por comas)</label>
            <input value={editing.keywords || ""} onChange={(e) => setEditing({ ...editing, keywords: e.target.value })} className="w-full px-3 py-2 text-sm border border-border rounded-sm" data-testid="page-keywords" />
            <label className="text-sm font-medium block">Contenido (HTML)</label>
            <textarea rows={8} value={editing.content || ""} onChange={(e) => setEditing({ ...editing, content: e.target.value })} className="w-full px-3 py-2 text-sm border border-border rounded-sm font-mono text-xs" data-testid="page-content" />
            <div className="border-t border-border pt-3">
              <p className="font-heading font-semibold text-sm mb-2">Bloques de contenido</p>
              <BlockEditor value={editing.blocks || []} onChange={(v) => setEditing({ ...editing, blocks: v })} testidPrefix="page-block" />
            </div>
            <button onClick={save} className="w-full bg-brand text-white py-2.5 rounded-sm font-semibold hover:bg-brand-dark" data-testid="save-page-btn">Guardar</button>
          </div>
        </div>
      )}
    </div>
  );
}
